import propertyTemplateService from "core/services/metaStoreService/property-template-service";
import {
    NUMERIC_TYPES,
    humanizeCondition,
    resolveTemplate,
    PropertyMapEntry,
    PropertyTemplate
} from "./get-tql-skeleton-for-source-or-target";

const PASSWORD_TYPE = "com.webaction.security.Password";

export interface DescribedProperty {
    name: string;
    // Simplified type the LLM can reason about: 'string' | 'number' | 'boolean' |
    // 'password' | or the raw Java type for anything more complex.
    type: string;
    required: boolean;
    // Present only when the property is conditionally required — the humanised
    // condition, e.g. "useConnectionProfile is 'false'".
    requiredWhen?: string;
    default?: string;
    // Enum values, when the property is constrained to a fixed set.
    allowedValues?: string[];
    // True for secret fields (passwords, keys) — never invent a value, always
    // emit a '<PLACEHOLDER>'.
    secret?: boolean;
    group?: string;
}

export interface DescribedTemplate {
    name: string;
    adapterType: string;
    requiresParser: boolean;
    requiresFormatter: boolean;
    properties: DescribedProperty[];
}

function simplifyType(type: string): string {
    if (type === PASSWORD_TYPE) return "password";
    if (type === "java.lang.Boolean") return "boolean";
    if (NUMERIC_TYPES.has(type)) return "number";
    if (type === "java.lang.String") return "string";
    return type;
}

function describeProperty(prop: PropertyMapEntry): DescribedProperty {
    const described: DescribedProperty = {
        name: prop.name,
        type: simplifyType(prop.type),
        required: !!prop.required
    };

    const requiredWhen = humanizeCondition(prop.conditionalRequired);
    if (requiredWhen) described.requiredWhen = requiredWhen;
    if (prop.defaultValue) described.default = prop.defaultValue;
    if (prop.values && prop.values.length > 0) described.allowedValues = prop.values;
    if (prop.type === PASSWORD_TYPE) described.secret = true;
    if (prop.group) described.group = prop.group;

    return described;
}

// Pure transform: shapes a raw property template into an LLM-friendly description.
// Unlike getTqlSkeleton (which only builds SOURCE / TARGET statements), this returns
// the plain field list so callers can build any statement shape — in particular a
// CONNECTIONPROFILE `TYPE <CPType>( ... )` block, which getTqlSkeleton does not cover.
export function buildPropertyTemplateDescription(template: PropertyTemplate): DescribedTemplate {
    return {
        name: template.name,
        adapterType: template.adapterType,
        requiresParser: !!template.requiresParser,
        requiresFormatter: !!template.requiresFormatter,
        properties: (template.propertyMap || []).map(describeProperty)
    };
}

function resolveTemplateCaseInsensitive(name: string): PropertyTemplate | null {
    const target = name.toLowerCase();
    const match = (propertyTemplateService.propertyTemplates || []).find(
        (model: any) => (model.get("name") || "").toLowerCase() === target
    );
    return match ? (match.toJSON() as PropertyTemplate) : null;
}

export function describePropertyTemplate(name: string): DescribedTemplate | string {
    const template = resolveTemplate(name) || resolveTemplateCaseInsensitive(name);
    if (!template) return `Property template '${name}' not found.`;
    return buildPropertyTemplateDescription(template);
}
